import React from "react";
import { resolveImg, uploadImage } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";

/**
 * Drag-and-drop image gallery for the admin property form.
 *
 * Features:
 *   - Drop a file (or multiple) anywhere on the dashed dropzone → uploads to /api/upload/image
 *   - Click the dropzone to open a file picker (same behaviour)
 *   - Click "+ ADD BY URL" to open a shadcn dialog and paste a remote image URL
 *   - Drag existing thumbnails to reorder — first image is the hero (shown on listing cards + detail hero)
 *   - "★ SET HERO" button on any non-first image promotes it to position 0
 *   - × button removes a single image; a toast lets you UNDO the removal
 *
 * Controlled component: parent owns the `images` array (a list of URLs).
 */
export default function AdminImageGallery({ images, onChange }) {
  const [uploading, setUploading] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [draggingIdx, setDraggingIdx] = React.useState(null);
  const [overIdx, setOverIdx] = React.useState(null);
  const [urlDialog, setUrlDialog] = React.useState({ open: false, value: "" });
  const fileInputRef = React.useRef(null);

  const uploadFiles = React.useCallback(async (files) => {
    const valid = files.filter((f) => f.type.startsWith("image/"));
    if (!valid.length) {
      toast.error("Only image files are accepted (JPG / PNG / WebP).");
      return;
    }
    setUploading(true);
    try {
      const urls = [];
      for (const f of valid) {
        const r = await uploadImage(f);
        urls.push(r.url);
      }
      onChange([...images, ...urls]);
      toast.success(`${urls.length} image(s) uploaded`);
    } catch (err) {
      toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  }, [images, onChange]);

  // ── Dropzone (file drop from OS) ────────────────────────────────────────
  const onDropZoneDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer.types?.includes("Files")) setIsDragging(true);
  };
  const onDropZoneDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };
  const onDropZoneDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) await uploadFiles(files);
  };

  // ── Thumbnail reordering (drag thumb to drop on another thumb) ──────────
  const onThumbDragStart = (i) => (e) => {
    setDraggingIdx(i);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(i));
  };
  const onThumbDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (i !== overIdx) setOverIdx(i);
  };
  const onThumbDrop = (i) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const from = draggingIdx;
    setDraggingIdx(null);
    setOverIdx(null);
    if (from === null || from === i) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    onChange(next);
  };
  const onThumbDragEnd = () => {
    setDraggingIdx(null);
    setOverIdx(null);
  };

  const removeAt = (i) => {
    const removed = images[i];
    const next = images.filter((_, idx) => idx !== i);
    onChange(next);
    toast.success("Image removed", {
      action: {
        label: "Undo",
        onClick: () => {
          const restored = [...next];
          restored.splice(i, 0, removed);
          onChange(restored);
        },
      },
    });
  };
  const setHero = (i) => {
    if (i === 0) return;
    const next = [...images];
    const [moved] = next.splice(i, 1);
    next.unshift(moved);
    onChange(next);
  };

  const confirmAddUrl = () => {
    const url = urlDialog.value.trim();
    if (url) onChange([...images, url]);
    setUrlDialog({ open: false, value: "" });
  };

  const onPickerChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) await uploadFiles(files);
  };

  return (
    <div data-testid="admin-image-gallery">
      {/* Existing images */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-3">
          {images.map((img, i) => {
            const isHero = i === 0;
            const isDraggingThis = draggingIdx === i;
            const isDropTarget = overIdx === i && draggingIdx !== null && draggingIdx !== i;
            return (
              <div
                key={`${img}-${i}`}
                data-testid={`gallery-thumb-${i}`}
                draggable
                onDragStart={onThumbDragStart(i)}
                onDragOver={onThumbDragOver(i)}
                onDrop={onThumbDrop(i)}
                onDragEnd={onThumbDragEnd}
                className={`relative w-28 h-28 border-2 cursor-move group transition-all
                  ${isHero ? "border-[#FFCC00]" : "border-zinc-200"}
                  ${isDraggingThis ? "opacity-40" : ""}
                  ${isDropTarget ? "border-[#031833] scale-105" : ""}`}
              >
                <img src={resolveImg(img)} alt={`Image ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />

                {isHero && (
                  <div className="absolute top-0 left-0 bg-[#FFCC00] text-[#031833] font-mono-tech text-[9px] uppercase px-1.5 py-0.5 tracking-wider">
                    ★ Hero
                  </div>
                )}

                {/* Hover controls */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-between p-1.5 opacity-0 group-hover:opacity-100">
                  {!isHero ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setHero(i); }}
                      data-testid={`set-hero-${i}`}
                      className="bg-[#FFCC00] text-[#031833] font-mono-tech text-[9px] uppercase px-1.5 py-1 tracking-wider hover:brightness-90"
                    >
                      ★ Set hero
                    </button>
                  ) : <span />}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                    data-testid={`remove-img-${i}`}
                    className="bg-zinc-950 text-white font-mono-tech text-xs px-1.5 py-0.5 hover:bg-red-600"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dropzone */}
      <div
        data-testid="admin-image-dropzone"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={onDropZoneDragOver}
        onDragLeave={onDropZoneDragLeave}
        onDrop={onDropZoneDrop}
        className={`border-2 border-dashed flex flex-col items-center justify-center py-8 px-4 cursor-pointer transition-colors text-center select-none
          ${isDragging ? "border-[#031833] bg-[#031833]/5" : "border-zinc-300 hover:border-[#031833]"}`}
      >
        <input
          ref={fileInputRef}
          data-testid="admin-image-upload"
          type="file"
          multiple
          accept="image/*"
          onChange={onPickerChange}
          className="hidden"
        />
        <div className="font-display text-lg tracking-tighter text-[#031833] mb-1">
          {uploading ? "UPLOADING…" : isDragging ? "DROP TO UPLOAD" : "DRAG & DROP IMAGES HERE"}
        </div>
        <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mb-3">
          or click to browse · JPG / PNG / WebP · max 10 MB each
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setUrlDialog({ open: true, value: "" }); }}
          data-testid="admin-add-img-url"
          className="font-mono-tech text-[10px] uppercase text-zinc-700 underline hover:text-[#031833]"
        >
          + add by URL
        </button>
      </div>

      {images.length > 1 && (
        <p className="font-mono-tech text-[10px] uppercase text-zinc-500 mt-2 tracking-wider">
          Tip: drag any thumbnail to reorder · the first image is the hero (shown on listing cards)
        </p>
      )}

      <Dialog open={urlDialog.open} onOpenChange={(o) => setUrlDialog((s) => ({ ...s, open: o }))}>
        <DialogContent data-testid="add-img-url-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tighter">Add image by URL</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="add-img-url-input"
            autoFocus
            value={urlDialog.value}
            onChange={(e) => setUrlDialog((s) => ({ ...s, value: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && confirmAddUrl()}
            placeholder="https://..."
            className="font-mono-tech text-sm"
          />
          <DialogFooter className="gap-2">
            <button
              type="button"
              data-testid="add-img-url-cancel"
              onClick={() => setUrlDialog({ open: false, value: "" })}
              className="font-mono-tech text-xs uppercase px-4 py-2 border border-zinc-300 hover:bg-zinc-100"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="add-img-url-confirm"
              onClick={confirmAddUrl}
              className="font-mono-tech text-xs uppercase px-4 py-2 bg-zinc-950 text-white hover:bg-[#031833]"
            >
              Add
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
